import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { api } from '../lib/api';
import { formatPrice } from '../lib/currency';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Loader2, Ban, ArrowLeft, Sparkles, Gauge, ShieldCheck, Megaphone, ScrollText,
  ListChecks, TrendingDown, Plane, Shuffle, ArrowRight, CheckCircle2, XCircle,
  Star, Eye, BarChart3, Lock, AlertTriangle, RefreshCw, FlaskConical,
} from 'lucide-react';
import { fmtDateTime } from '../lib/dateFormat';

const TYPE_META = {
  INTERMEDIATE_DESTINATION: { label: 'Intermediate destination', icon: Plane, short: 'Intermediate' },
  SELF_TRANSFER: { label: 'Self-transfer', icon: Shuffle, short: 'Self-transfer' },
  ALTERNATE_AIRPORT: { label: 'Alternate airport', icon: Plane, short: 'Alternate' },
};
const RISK_CLS = { Low: 'bg-emerald-500/15 text-emerald-700', Moderate: 'bg-amber-500/15 text-amber-700', High: 'bg-destructive/15 text-destructive' };

const WARNING_TEXT = {
  INTERMEDIATE_DESTINATION: 'This itinerary is ticketed to a further final city — the traveller exits at the intended stop. Review airline policy and checked-baggage implications before approving.',
  SELF_TRANSFER: 'Self-transfer between separate tickets. The connection is not protected by the airline — verify the layover buffer and re-check-in requirements.',
  ALTERNATE_AIRPORT: 'Uses a nearby alternate airport. Confirm ground transfer time and cost for the traveller.',
};

const SmartFareWarning = ({ type }) => {
  const text = WARNING_TEXT[type];
  if (!text) return null;
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2" data-testid="smart-fare-warning">
      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
      <p className="text-xs text-amber-800">{text}</p>
    </div>
  );
};
const STATUS_CLS = {
  DETECTED: 'bg-blue-500/15 text-blue-700', REVIEWED: 'bg-violet-500/15 text-violet-700',
  APPROVED: 'bg-emerald-500/15 text-emerald-700', SUPPRESSED: 'bg-muted text-muted-foreground',
  EXPIRED: 'bg-amber-500/15 text-amber-700',
};

export default function FareIntelligence() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [role, setRole] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => { document.title = 'Admin · Fare Intelligence | FlightDeals.in'; if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);
  useEffect(() => { if (!user) return; api.roleMe().then(({ data }) => setRole(data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); }); }, [user]);

  if (loading) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;
  if (denied) return <Layout><div className="max-w-lg mx-auto py-24 text-center"><Ban className="h-10 w-10 mx-auto mb-2 text-amber-500" /><h1 className="font-display text-2xl font-semibold">Staff access required</h1><p className="text-muted-foreground mt-1">Fare Intelligence is available to editors and admins.</p></div></Layout>;

  const isEditor = role?.role === 'editor';
  const canAdmin = role && role.role !== 'editor';

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/admin')} className="text-sm text-muted-foreground hover:text-accent flex items-center gap-1 mb-3" data-testid="fi-back"><ArrowLeft className="h-4 w-4" /> Back to dashboard</button>
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><Sparkles className="h-6 w-6 text-accent" /> Smart Fare Intelligence</h1>
          <div className="flex items-center gap-2">
            {role && <Badge className="rounded-full bg-accent/15 text-accent" data-testid="fi-role-badge">{role.role.replace('_', ' ')}</Badge>}
            <Button variant="outline" className="rounded-xl" onClick={() => navigate('/admin/fare-intelligence/analytics')} data-testid="fi-analytics-link"><BarChart3 className="h-4 w-4 mr-1.5" />Analytics</Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Review, merchandise and govern Smart Fare opportunities. Algorithmic truth scores are never altered by editorial or sponsorship actions.</p>

        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-muted-foreground flex items-start gap-2 mb-6">
          <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
          <span><strong className="text-amber-700">Truth vs merchandising:</strong> Approving, featuring or sponsoring a Smart Fare changes only <em>where</em> and <em>how prominently</em> it appears — never its score, saving, risk or price. Mandatory safety floors always apply.</span>
        </div>

        <Tabs defaultValue="overview" data-testid="fi-tabs">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="fi-tab-overview"><Gauge className="h-4 w-4 mr-1.5" />Overview</TabsTrigger>
            <TabsTrigger value="queue" data-testid="fi-tab-queue"><ListChecks className="h-4 w-4 mr-1.5" />Opportunities</TabsTrigger>
            <TabsTrigger value="sponsored" data-testid="fi-tab-sponsored"><Megaphone className="h-4 w-4 mr-1.5" />Sponsored</TabsTrigger>
            <TabsTrigger value="settings" data-testid="fi-tab-settings"><ShieldCheck className="h-4 w-4 mr-1.5" />Settings</TabsTrigger>
            <TabsTrigger value="audit" data-testid="fi-tab-audit"><ScrollText className="h-4 w-4 mr-1.5" />Audit log</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4"><Overview /></TabsContent>
          <TabsContent value="queue" className="mt-4"><OpportunityQueue canAdmin={canAdmin} isEditor={isEditor} /></TabsContent>
          <TabsContent value="sponsored" className="mt-4"><Sponsored canAdmin={canAdmin} /></TabsContent>
          <TabsContent value="settings" className="mt-4"><SettingsPanel canAdmin={canAdmin} /></TabsContent>
          <TabsContent value="audit" className="mt-4"><AuditLog /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

/* ============================ Overview ============================ */
const KPI = ({ icon: Icon, label, value, testId, sub }) => (
  <Card className="p-4 rounded-2xl" data-testid={testId}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Icon className="h-4 w-4 text-accent" /> {label}</div>
    <p className="text-2xl font-semibold tabular">{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
  </Card>
);

const DistBar = ({ label, value, total, cls }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between text-xs"><span className="text-muted-foreground">{label}</span><span className="font-semibold tabular">{value}</span></div>
    <Progress value={total ? (value / total) * 100 : 0} className={`h-2 ${cls || ''}`} />
  </div>
);

function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.fiDashboard(true).then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <div className="grid gap-3 grid-cols-2 md:grid-cols-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>;
  const o = d.overview;
  const riskTotal = Object.values(d.by_risk).reduce((a, b) => a + b, 0);
  const typeTotal = Object.values(d.by_type).reduce((a, b) => a + b, 0);
  const statusTotal = Object.values(d.by_status).reduce((a, b) => a + b, 0);
  return (
    <div className="space-y-6" data-testid="fi-overview">
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4" data-testid="fi-kpis">
        <KPI icon={ListChecks} label="Total opportunities" value={o.total} testId="fi-kpi-total" sub={`${o.demo_total} demo`} />
        <KPI icon={CheckCircle2} label="Active (not expired)" value={o.active} testId="fi-kpi-active" />
        <KPI icon={Gauge} label="Avg truth score" value={`${o.avg_score}/100`} testId="fi-kpi-score" />
        <KPI icon={TrendingDown} label="Avg saving" value={formatPrice(o.avg_saving, 'INR')} testId="fi-kpi-saving" sub={`${o.avg_saving_pct}% avg`} />
        <KPI icon={Megaphone} label="Active placements" value={o.active_placements} testId="fi-kpi-placements" />
        <KPI icon={Star} label="Featured" value={o.featured} testId="fi-kpi-featured" />
        <KPI icon={Eye} label="Impressions" value={d.metrics.smart_fare_impression} testId="fi-kpi-impressions" sub="production only" />
        <KPI icon={MousePointerish} label="Partner clicks" value={d.metrics.smart_fare_partner_click} testId="fi-kpi-clicks" sub="production only" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5 rounded-2xl" data-testid="fi-dist-type">
          <h3 className="font-semibold mb-3 text-sm flex items-center gap-1.5"><Plane className="h-4 w-4 text-accent" />Detector distribution</h3>
          <div className="space-y-3">
            {Object.entries(d.by_type).map(([k, v]) => <DistBar key={k} label={TYPE_META[k]?.short || k} value={v} total={typeTotal} />)}
          </div>
        </Card>
        <Card className="p-5 rounded-2xl" data-testid="fi-dist-risk">
          <h3 className="font-semibold mb-3 text-sm flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-accent" />Risk distribution</h3>
          <div className="space-y-3">
            {Object.entries(d.by_risk).map(([k, v]) => <DistBar key={k} label={k} value={v} total={riskTotal} />)}
          </div>
        </Card>
        <Card className="p-5 rounded-2xl" data-testid="fi-dist-status">
          <h3 className="font-semibold mb-3 text-sm flex items-center gap-1.5"><ListChecks className="h-4 w-4 text-accent" />Status distribution</h3>
          <div className="space-y-3">
            {Object.entries(d.by_status).map(([k, v]) => <DistBar key={k} label={k} value={v} total={statusTotal} />)}
          </div>
        </Card>
      </div>

      <Card className="p-5 rounded-2xl" data-testid="fi-recent">
        <h3 className="font-semibold mb-3 text-sm">Recent Smart Fare activity</h3>
        <div className="space-y-2">
          {d.recent.map((r) => (
            <div key={r.id} className="flex items-center gap-3 text-sm border-b border-border last:border-0 pb-2 last:pb-0">
              <Badge className={`rounded-full text-[10px] ${STATUS_CLS[r.status] || ''}`}>{r.status}</Badge>
              <span className="font-medium">{r.origin} → {r.requested_destination}</span>
              <span className="text-xs text-muted-foreground">{TYPE_META[r.type]?.short}</span>
              {r.is_demo && <Badge variant="outline" className="rounded-full text-[9px] border-dashed"><FlaskConical className="h-2.5 w-2.5 mr-0.5" />DEMO</Badge>}
              <span className="ml-auto text-xs text-muted-foreground">Score {r.smart_fare_score} · {formatPrice(r.potential_saving, 'INR')}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
// small inline icon alias (avoid extra import churn)
function MousePointerish(props) { return <TrendingDown {...props} />; }

/* ============================ Opportunity Queue ============================ */
function OpportunityQueue({ canAdmin, isEditor }) {
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({ status: '', type: '', risk: '', demo: '' });
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(() => {
    const f = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) f[k] = v; });
    api.fiOpportunities(f).then(({ data }) => setItems(data.opportunities)).catch(() => setItems([]));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, body, msg) => {
    try { await api.updateOpportunity(id, body); toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  return (
    <div className="space-y-4" data-testid="fi-queue">
      <Card className="p-4 rounded-2xl flex flex-wrap gap-3 items-end">
        <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })}
          options={[['', 'All'], ['DETECTED', 'Detected'], ['REVIEWED', 'Reviewed'], ['APPROVED', 'Approved'], ['SUPPRESSED', 'Suppressed']]} testId="fi-filter-status" />
        <FilterSelect label="Type" value={filters.type} onChange={(v) => setFilters({ ...filters, type: v })}
          options={[['', 'All'], ['INTERMEDIATE_DESTINATION', 'Intermediate'], ['SELF_TRANSFER', 'Self-transfer'], ['ALTERNATE_AIRPORT', 'Alternate']]} testId="fi-filter-type" />
        <FilterSelect label="Risk" value={filters.risk} onChange={(v) => setFilters({ ...filters, risk: v })}
          options={[['', 'All'], ['Low', 'Low'], ['Moderate', 'Moderate'], ['High', 'High']]} testId="fi-filter-risk" />
        <FilterSelect label="Demo" value={filters.demo} onChange={(v) => setFilters({ ...filters, demo: v })}
          options={[['', 'All'], ['true', 'Demo only'], ['false', 'Production only']]} testId="fi-filter-demo" />
        <Button variant="outline" className="rounded-xl ml-auto" onClick={load} data-testid="fi-refresh"><RefreshCw className="h-4 w-4 mr-1.5" />Refresh</Button>
      </Card>

      {!items ? <Skeleton className="h-40 rounded-2xl" /> : items.length === 0 ? (
        <Card className="p-12 rounded-2xl text-center"><ListChecks className="h-10 w-10 mx-auto text-muted-foreground mb-2" /><p className="font-semibold">No opportunities match these filters</p></Card>
      ) : (
        <div className="space-y-2" data-testid="fi-opp-list">
          {items.map((o) => {
            const Meta = TYPE_META[o.type] || TYPE_META.INTERMEDIATE_DESTINATION;
            return (
              <Card key={o.id} className="p-4 rounded-2xl" data-testid={`fi-opp-${o.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{o.origin} → {o.requested_destination}</span>
                      <Badge variant="outline" className="rounded-full text-[10px]"><Meta.icon className="h-3 w-3 mr-1" />{Meta.short}</Badge>
                      <Badge className={`rounded-full text-[10px] ${STATUS_CLS[o.status] || ''}`}>{o.status}</Badge>
                      {o.featured && <Badge className="rounded-full text-[10px] bg-accent/15 text-accent"><Star className="h-3 w-3 mr-0.5" />Featured</Badge>}
                      {o.sponsored && <Badge className="rounded-full text-[10px] bg-violet-500/15 text-violet-700"><Megaphone className="h-3 w-3 mr-0.5" />{o.sponsored.sponsor_label}</Badge>}
                      {o.is_demo && <Badge variant="outline" className="rounded-full text-[9px] border-dashed"><FlaskConical className="h-2.5 w-2.5 mr-0.5" />DEMO</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Ticketed to {o.ticketed_final_city || o.ticketed_final_destination} · {o.airline_name} · via {o.provider}</p>
                  </div>
                  <div className="flex items-center gap-3 text-right">
                    <div>
                      <p className="text-[10px] text-muted-foreground">Truth score</p>
                      <p className="font-semibold tabular" data-testid={`fi-opp-score-${o.id}`}>{o.smart_fare_score}/100</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">Saving</p>
                      <p className="font-semibold tabular text-emerald-700">{formatPrice(o.potential_saving, 'INR')}</p>
                    </div>
                    <Badge className={`rounded-full text-[10px] ${RISK_CLS[o.risk_level] || ''}`}>{o.risk_level}</Badge>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setDetailId(o.id)} data-testid={`fi-view-${o.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Details</Button>
                  {canAdmin && o.status !== 'APPROVED' && <Button size="sm" className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act(o.id, { status: 'APPROVED' }, 'Approved')} data-testid={`fi-approve-${o.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve</Button>}
                  {canAdmin && o.status !== 'REVIEWED' && o.status !== 'APPROVED' && <Button size="sm" variant="outline" className="rounded-lg" onClick={() => act(o.id, { status: 'REVIEWED' }, 'Marked reviewed')} data-testid={`fi-review-${o.id}`}>Review</Button>}
                  {canAdmin && o.status !== 'SUPPRESSED' && <Button size="sm" variant="outline" className="rounded-lg text-destructive hover:text-destructive" onClick={() => act(o.id, { status: 'SUPPRESSED' }, 'Suppressed')} data-testid={`fi-suppress-${o.id}`}><XCircle className="h-3.5 w-3.5 mr-1" />Suppress</Button>}
                  {canAdmin && (o.featured
                    ? <Button size="sm" variant="outline" className="rounded-lg" onClick={() => act(o.id, { featured: false }, 'Unfeatured')} data-testid={`fi-unfeature-${o.id}`}>Unfeature</Button>
                    : <Button size="sm" variant="outline" className="rounded-lg" onClick={() => act(o.id, { featured: true }, 'Featured')} data-testid={`fi-feature-${o.id}`}><Star className="h-3.5 w-3.5 mr-1" />Feature</Button>)}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <OpportunityDetail id={detailId} open={!!detailId} onClose={() => setDetailId(null)} onSaved={load} canAdmin={canAdmin} isEditor={isEditor} />
    </div>
  );
}

const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <div>
    <Label className="mb-1 block text-xs">{label}</Label>
    <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
      <SelectTrigger className="w-40 h-9" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v || 'all'} value={v || 'all'}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

function OpportunityDetail({ id, open, onClose, onSaved, canAdmin, isEditor }) {
  const [data, setData] = useState(null);
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => {
    if (!id) { setData(null); return; }
    setData(null);
    api.fiOpportunityDetail(id).then(({ data }) => { setData(data); setLabel(data.opportunity.editorial_label || ''); setNote(''); }).catch(() => {});
  }, [id]);

  const o = data?.opportunity;
  const saveEditorial = async () => {
    try { await api.updateOpportunity(id, { editorial_label: label, admin_note: note || undefined }); toast.success('Saved editorial'); setNote(''); onSaved(); api.fiOpportunityDetail(id).then(({ data }) => setData(data)); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="fi-opp-detail">
        <SheetHeader><SheetTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-accent" />Opportunity detail</SheetTitle></SheetHeader>
        {!o ? <div className="py-12 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div> : (
          <div className="space-y-4 mt-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`rounded-full text-[10px] ${STATUS_CLS[o.status] || ''}`}>{o.status}</Badge>
              <Badge variant="outline" className="rounded-full text-[10px]">{TYPE_META[o.type]?.label}</Badge>
              {o.is_demo && <Badge variant="outline" className="rounded-full text-[9px] border-dashed"><FlaskConical className="h-2.5 w-2.5 mr-0.5" />DEMO</Badge>}
            </div>

            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-2.5 text-[11px] text-muted-foreground flex items-start gap-1.5">
              <Lock className="h-3.5 w-3.5 mt-0.5 text-amber-600 shrink-0" /> These algorithmic values are read-only and cannot be edited by admins or sponsors.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <RF label="Truth score" value={`${o.smart_fare_score}/100 · ${o.score_label}`} />
              <RF label="Risk" value={o.risk_level} />
              <RF label="Saving" value={`${formatPrice(o.potential_saving, 'INR')} (${o.saving_percentage}%)`} />
              <RF label="Smart / normal" value={`${formatPrice(o.smart_price, 'INR')} / ${formatPrice(o.normal_price, 'INR')}`} />
              <RF label="Feasibility / confidence" value={`${o.feasibility_score} / ${o.data_confidence}`} />
              <RF label="Provider" value={`${o.provider}${o.is_mock ? ' (mock)' : ''}`} />
            </div>

            <div className="rounded-xl border border-border p-3">
              <p className="text-[11px] text-muted-foreground mb-1.5">Full ticketed itinerary — ends at {o.ticketed_final_city || o.ticketed_final_destination}</p>
              <div className="flex items-center flex-wrap gap-1.5 text-sm font-medium">
                {(o.segments || []).map((s, i) => (
                  <React.Fragment key={i}>
                    {i === 0 && <span>{s.origin}</span>}
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className={o.intermediate_airport === s.destination ? 'text-accent' : ''}>{s.destination}</span>
                  </React.Fragment>
                ))}
              </div>
              {o.type === 'INTERMEDIATE_DESTINATION' && <p className="text-[11px] text-muted-foreground mt-2">Intended exit: <strong>{o.intermediate_airport}</strong> · Ticketed final: <strong>{o.ticketed_final_destination}</strong></p>}
              {o.type === 'SELF_TRANSFER' && <p className="text-[11px] text-muted-foreground mt-2">Connection via <strong>{o.intermediate_airport}</strong> · {o.connection_minutes}min (buffer {o.recommended_buffer_minutes}min)</p>}
            </div>

            <SmartFareWarning type={o.type} />

            {data.placements?.length > 0 && (
              <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-3">
                <p className="text-sm font-semibold flex items-center gap-1.5"><Megaphone className="h-4 w-4 text-violet-600" />Sponsored placements</p>
                {data.placements.map((p) => (
                  <div key={p.placement_id} className="text-xs text-muted-foreground mt-1.5 flex items-center gap-2">
                    <Badge variant="outline" className="rounded-full text-[9px]">{p.surface}</Badge>{p.campaign_name} · {p.sponsor_label} · priority {p.priority} · <span className={p.status === 'active' ? 'text-emerald-600' : ''}>{p.status}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="rounded-xl border border-border p-3 space-y-2">
              <p className="text-sm font-semibold flex items-center gap-1.5"><ScrollText className="h-4 w-4 text-accent" />Editorial (does not affect score)</p>
              <div><Label className="text-xs">Editorial label</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Great for Phuket beaches" data-testid="fi-editorial-label" /></div>
              <div><Label className="text-xs">Internal note</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note to the audit trail…" data-testid="fi-internal-note" /></div>
              <Button size="sm" className="rounded-lg bg-accent text-accent-foreground hover:bg-accent/90" onClick={saveEditorial} data-testid="fi-save-editorial">Save editorial</Button>
            </div>

            {data.audit?.length > 0 && (
              <div className="rounded-xl border border-border p-3">
                <p className="text-sm font-semibold mb-1.5">Audit trail</p>
                <div className="space-y-1 max-h-40 overflow-auto">
                  {data.audit.map((a) => (
                    <div key={a.audit_id} className="text-[11px] text-muted-foreground flex items-center gap-2">
                      <Badge variant="outline" className="rounded-full text-[9px]">{a.action}</Badge>{a.actor_email} · {fmtDateTime(a.created_at)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
const RF = ({ label, value }) => (<div><p className="text-[11px] text-muted-foreground">{label}</p><p className="text-sm font-medium">{value ?? '—'}</p></div>);

/* ============================ Sponsored ============================ */
const SURFACE_LABELS = { smart_fares: 'Smart Fares', flight_results: 'Flight results', homepage: 'Homepage', trending: 'Trending', destination: 'Destination pages', deals: 'Deals', search_results: 'Search results', creator_itineraries: 'Creator itineraries' };
const ALL_SURFACES = Object.keys(SURFACE_LABELS);
const DETECTORS = ['INTERMEDIATE_DESTINATION', 'SELF_TRANSFER', 'ALTERNATE_AIRPORT'];

function Sponsored({ canAdmin }) {
  const [camps, setCamps] = useState(null);
  const [edit, setEdit] = useState(null);
  const [placements, setPlacements] = useState(null);

  const load = () => api.fiCampaigns().then(({ data }) => setCamps(data.campaigns)).catch(() => setCamps([]));
  const loadPl = () => api.fiPlacements().then(({ data }) => setPlacements(data.placements)).catch(() => setPlacements([]));
  useEffect(() => { load(); loadPl(); }, []);

  const distribute = async (id) => {
    try { const { data } = await api.fiDistributeCampaign(id); toast.success(`Distributed: ${data.placements} placements across ${data.eligible} opportunities`); loadPl(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Distribute failed'); }
  };
  const setPlStatus = async (pid, status) => {
    try { await api.fiSetPlacementStatus(pid, status); toast.success(`Placement ${status}`); loadPl(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="fi-sponsored">
      <div className="rounded-xl border border-border bg-secondary/40 p-3 text-xs text-muted-foreground flex items-start gap-2">
        <Megaphone className="h-4 w-4 mt-0.5 text-accent shrink-0" /> Sponsorship reuses existing campaigns. It controls placement surface & priority only — never the truth score, saving or risk of a Smart Fare. Sponsored cards are always clearly labelled to users.
      </div>

      <div>
        <h3 className="font-semibold text-sm mb-2">Campaigns with Smart Fare targeting</h3>
        {!camps ? <Skeleton className="h-24 rounded-2xl" /> : camps.length === 0 ? (
          <Card className="p-8 rounded-2xl text-center text-sm text-muted-foreground">No campaigns yet. Create one in Campaigns, then configure Smart Fare targeting here.</Card>
        ) : (
          <div className="space-y-2" data-testid="fi-campaign-list">
            {camps.map((c) => (
              <Card key={c.campaign_id} className="p-4 rounded-2xl" data-testid={`fi-campaign-${c.campaign_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{c.name}</span>
                  {c.brand && <Badge variant="outline" className="rounded-full text-[10px]">{c.brand}</Badge>}
                  <Badge className="rounded-full text-[10px] bg-muted">{c.status}</Badge>
                  {c.smart_fare_enabled ? <Badge className="rounded-full text-[10px] bg-emerald-500/15 text-emerald-700">Smart Fare ON</Badge> : <Badge variant="outline" className="rounded-full text-[10px]">Smart Fare off</Badge>}
                  {c.is_demo && <Badge variant="outline" className="rounded-full text-[9px] border-dashed"><FlaskConical className="h-2.5 w-2.5 mr-0.5" />DEMO</Badge>}
                  <span className="ml-auto text-[11px] text-muted-foreground">{c.placement_count} placements · {c.start_date} → {c.end_date}</span>
                </div>
                {canAdmin && (
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setEdit(c)} data-testid={`fi-edit-targeting-${c.campaign_id}`}>Edit targeting</Button>
                    <Button size="sm" className="rounded-lg bg-accent text-accent-foreground hover:bg-accent/90" disabled={!c.smart_fare_enabled} onClick={() => distribute(c.campaign_id)} data-testid={`fi-distribute-${c.campaign_id}`}><RefreshCw className="h-3.5 w-3.5 mr-1" />Distribute</Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-semibold text-sm mb-2">Sponsored Smart Fare placements</h3>
        {!placements ? <Skeleton className="h-24 rounded-2xl" /> : placements.length === 0 ? (
          <Card className="p-8 rounded-2xl text-center text-sm text-muted-foreground">No placements yet. Configure targeting on a campaign and click Distribute.</Card>
        ) : (
          <Card className="p-0 rounded-2xl overflow-hidden" data-testid="fi-placement-list">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2 px-4">Campaign</th><th>Smart Fare</th><th>Surface</th><th>Priority</th><th>Truth</th><th className="px-4">Status</th></tr></thead>
              <tbody>
                {placements.map((p) => (
                  <tr key={p.placement_id} className="border-b border-border last:border-0" data-testid={`fi-placement-${p.placement_id}`}>
                    <td className="py-2.5 px-4"><p className="font-medium">{p.campaign_name}</p><p className="text-[10px] text-muted-foreground">{p.sponsor_label}{p.is_demo ? ' · DEMO' : ''}</p></td>
                    <td className="text-xs font-mono">{p.smart_fare_id}</td>
                    <td><Badge variant="outline" className="rounded-full text-[10px]">{SURFACE_LABELS[p.surface] || p.surface}</Badge></td>
                    <td className="tabular">{p.priority}</td>
                    <td className="tabular text-xs">{p.truth_score_snapshot}/100 · {p.risk_snapshot}</td>
                    <td className="px-4">
                      {canAdmin ? (
                        <Select value={p.status} onValueChange={(v) => setPlStatus(p.placement_id, v)}>
                          <SelectTrigger className="w-32 h-8" data-testid={`fi-placement-status-${p.placement_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{['active', 'paused', 'suppressed'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <Badge className="rounded-full text-[10px]">{p.status}</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      <TargetingDialog campaign={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}

function TargetingDialog({ campaign, onClose, onSaved }) {
  const [t, setT] = useState(null);
  useEffect(() => {
    if (!campaign) { setT(null); return; }
    const base = campaign.smart_fare_targeting || {};
    setT({
      enabled: !!base.enabled,
      destinations: (base.destinations || []).join(', '),
      countries: (base.countries || []).join(', '),
      airports: (base.airports || []).join(', '),
      detectors: base.detectors || [...DETECTORS],
      min_score: base.min_score ?? 70,
      max_risk: base.max_risk || 'Moderate',
      surfaces: base.surfaces || ['smart_fares'],
      priority: base.priority ?? 50,
      featured: !!base.featured,
      sponsor_label: base.sponsor_label || (campaign.brand ? `Promoted by ${campaign.brand}` : 'Sponsored'),
      cta: base.cta || '',
    });
  }, [campaign]);

  const save = async () => {
    const payload = {
      enabled: t.enabled,
      destinations: t.destinations.split(',').map((s) => s.trim()).filter(Boolean),
      countries: t.countries.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean),
      airports: t.airports.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean),
      detectors: t.detectors,
      min_score: Number(t.min_score) || 0,
      max_risk: t.max_risk,
      surfaces: t.surfaces,
      priority: Number(t.priority) || 0,
      featured: t.featured,
      sponsor_label: t.sponsor_label,
      cta: t.cta,
    };
    try { await api.fiSetCampaignTargeting(campaign.campaign_id, payload); toast.success('Targeting saved'); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const toggle = (arr, v) => arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  return (
    <Dialog open={!!campaign} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="fi-targeting-dialog">
        <DialogHeader><DialogTitle>Smart Fare targeting — {campaign?.name}</DialogTitle></DialogHeader>
        {t && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm"><Switch checked={t.enabled} onCheckedChange={(v) => setT({ ...t, enabled: v })} data-testid="fi-targeting-enabled" /> Sponsor Smart Fares with this campaign</label>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Destinations (comma sep)</Label><Input value={t.destinations} onChange={(e) => setT({ ...t, destinations: e.target.value })} placeholder="thailand, bangkok" data-testid="fi-t-destinations" /></div>
              <div><Label className="text-xs">Countries (ISO)</Label><Input value={t.countries} onChange={(e) => setT({ ...t, countries: e.target.value })} placeholder="TH" data-testid="fi-t-countries" /></div>
              <div><Label className="text-xs">Airports</Label><Input value={t.airports} onChange={(e) => setT({ ...t, airports: e.target.value })} placeholder="BKK, HKT" data-testid="fi-t-airports" /></div>
              <div><Label className="text-xs">Min truth score</Label><Input type="number" value={t.min_score} onChange={(e) => setT({ ...t, min_score: e.target.value })} data-testid="fi-t-minscore" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Max risk</Label>
                <Select value={t.max_risk} onValueChange={(v) => setT({ ...t, max_risk: v })}><SelectTrigger data-testid="fi-t-maxrisk"><SelectValue /></SelectTrigger><SelectContent>{['Low', 'Moderate', 'High'].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs">Merch priority</Label><Input type="number" value={t.priority} onChange={(e) => setT({ ...t, priority: e.target.value })} data-testid="fi-t-priority" /></div>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Detectors</Label>
              <div className="flex flex-wrap gap-3">
                {DETECTORS.map((d) => <label key={d} className="flex items-center gap-1.5 text-xs"><Checkbox checked={t.detectors.includes(d)} onCheckedChange={() => setT({ ...t, detectors: toggle(t.detectors, d) })} data-testid={`fi-t-detector-${d}`} />{TYPE_META[d].short}</label>)}
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Placement surfaces</Label>
              <div className="flex flex-wrap gap-3">
                {ALL_SURFACES.map((s) => <label key={s} className="flex items-center gap-1.5 text-xs"><Checkbox checked={t.surfaces.includes(s)} onCheckedChange={() => setT({ ...t, surfaces: toggle(t.surfaces, s) })} data-testid={`fi-t-surface-${s}`} />{SURFACE_LABELS[s]}</label>)}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Sponsor label</Label><Input value={t.sponsor_label} onChange={(e) => setT({ ...t, sponsor_label: e.target.value })} data-testid="fi-t-label" /></div>
              <div><Label className="text-xs">CTA</Label><Input value={t.cta} onChange={(e) => setT({ ...t, cta: e.target.value })} placeholder="Explore with Klook" data-testid="fi-t-cta" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={t.featured} onCheckedChange={(v) => setT({ ...t, featured: v })} data-testid="fi-t-featured" /> Mark placements as featured</label>
          </div>
        )}
        <DialogFooter><Button onClick={save} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="fi-targeting-save">Save targeting</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================ Settings ============================ */
function SettingsPanel({ canAdmin }) {
  const [s, setS] = useState(null);
  const [floors, setFloors] = useState({});
  const load = () => api.fiSettings().then(({ data }) => { setS(data.settings); setFloors(data.mandatory_floors || {}); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!s) return <Skeleton className="h-64 rounded-2xl" />;

  const num = (k, v) => setS({ ...s, [k]: v === '' ? '' : Number(v) });
  const buf = (k, v) => setS({ ...s, buffers: { ...s.buffers, [k]: Number(v) || 0 } });
  const toggleDet = (d) => { const cur = s.allowed_detectors || DETECTORS; setS({ ...s, allowed_detectors: cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d] }); };

  const save = async () => {
    try { await api.updateFiSettings(s); toast.success('Settings saved (mandatory floors still enforced)'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Only admins can change settings'); }
  };

  const NF = ({ label, k, min }) => (<div><Label className="text-xs">{label}</Label><Input type="number" value={s[k] ?? ''} disabled={!canAdmin} onChange={(e) => num(k, e.target.value)} data-testid={`fi-set-${k}`} />{min != null && <p className="text-[10px] text-muted-foreground mt-0.5">Floor: {min}m</p>}</div>);

  return (
    <div className="space-y-5" data-testid="fi-settings">
      {!canAdmin && <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 flex items-center gap-2"><Lock className="h-4 w-4" /> Read-only: editors cannot change Smart Fare safety settings.</div>}

      <Card className="p-5 rounded-2xl space-y-4" data-testid="fi-settings-general">
        <div className="flex items-center justify-between"><h3 className="font-semibold text-sm">General</h3>
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!s.enabled} disabled={!canAdmin} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="fi-set-enabled" /> Smart Fare enabled</label>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <NF label="Min saving (₹)" k="min_saving_abs" />
          <NF label="Min saving %" k="min_saving_pct" />
          <NF label="Min truth score" k="min_score" />
          <NF label="Max per search" k="max_per_search" />
        </div>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!s.intermediate_enabled} disabled={!canAdmin} onCheckedChange={(v) => setS({ ...s, intermediate_enabled: v })} data-testid="fi-set-intermediate" /> Intermediate</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!s.self_transfer_enabled} disabled={!canAdmin} onCheckedChange={(v) => setS({ ...s, self_transfer_enabled: v })} data-testid="fi-set-selftransfer" /> Self-transfer</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!s.alternate_enabled} disabled={!canAdmin} onCheckedChange={(v) => setS({ ...s, alternate_enabled: v })} data-testid="fi-set-alternate" /> Alternate airport</label>
        </div>
      </Card>

      <Card className="p-5 rounded-2xl space-y-4" data-testid="fi-settings-safety">
        <h3 className="font-semibold text-sm flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-accent" />Self-transfer safety & connection buffers</h3>
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 text-[11px] text-amber-700 flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> Mandatory floors below are always enforced. Settings can only make connections safer, never unsafe.</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-xs">Buffer Dom→Dom (m)</Label><Input type="number" value={s.buffers?.dom_dom ?? ''} disabled={!canAdmin} onChange={(e) => buf('dom_dom', e.target.value)} data-testid="fi-buf-dom_dom" /><p className="text-[10px] text-muted-foreground mt-0.5">Floor: {floors.dom_dom}m</p></div>
          <div><Label className="text-xs">Buffer Dom→Intl (m)</Label><Input type="number" value={s.buffers?.dom_intl ?? ''} disabled={!canAdmin} onChange={(e) => buf('dom_intl', e.target.value)} data-testid="fi-buf-dom_intl" /><p className="text-[10px] text-muted-foreground mt-0.5">Floor: {floors.dom_intl}m</p></div>
          <div><Label className="text-xs">Buffer Intl→Dom (m)</Label><Input type="number" value={s.buffers?.intl_dom ?? ''} disabled={!canAdmin} onChange={(e) => buf('intl_dom', e.target.value)} data-testid="fi-buf-intl_dom" /><p className="text-[10px] text-muted-foreground mt-0.5">Floor: {floors.intl_dom}m</p></div>
          <div><Label className="text-xs">Buffer Intl→Intl (m)</Label><Input type="number" value={s.buffers?.intl_intl ?? ''} disabled={!canAdmin} onChange={(e) => buf('intl_intl', e.target.value)} data-testid="fi-buf-intl_intl" /><p className="text-[10px] text-muted-foreground mt-0.5">Floor: {floors.intl_intl}m</p></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <NF label="Min connection (m)" k="min_connection_minutes" />
          <NF label="Airport-change buffer (m)" k="airport_change_buffer" />
          <NF label="Self-transfer buffer (m)" k="self_transfer_buffer" />
          <NF label="Max itinerary complexity" k="max_itinerary_complexity" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Max accepted risk</Label>
            <Select value={s.max_accepted_risk || 'High'} disabled={!canAdmin} onValueChange={(v) => setS({ ...s, max_accepted_risk: v })}><SelectTrigger data-testid="fi-set-maxrisk"><SelectValue /></SelectTrigger><SelectContent>{['Low', 'Moderate', 'High'].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs mb-1 block">Allowed detectors</Label>
            <div className="flex flex-wrap gap-3 pt-1.5">{DETECTORS.map((d) => <label key={d} className="flex items-center gap-1.5 text-xs"><Checkbox checked={(s.allowed_detectors || DETECTORS).includes(d)} disabled={!canAdmin} onCheckedChange={() => toggleDet(d)} data-testid={`fi-set-det-${d}`} />{TYPE_META[d].short}</label>)}</div>
          </div>
        </div>
      </Card>

      {canAdmin && <Button onClick={save} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="fi-settings-save"><ShieldCheck className="h-4 w-4 mr-1.5" />Save settings</Button>}
    </div>
  );
}

/* ============================ Audit log ============================ */
function AuditLog() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.fiAudit().then(({ data }) => setItems(data.audit)).catch(() => setItems([])); }, []);
  if (!items) return <Skeleton className="h-40 rounded-2xl" />;
  if (items.length === 0) return <Card className="p-12 rounded-2xl text-center"><ScrollText className="h-10 w-10 mx-auto text-muted-foreground mb-2" /><p className="font-semibold">No admin actions recorded yet</p></Card>;
  return (
    <Card className="p-0 rounded-2xl overflow-hidden" data-testid="fi-audit-list">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2 px-4">Action</th><th>Entity</th><th>Actor</th><th className="px-4">When</th></tr></thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.audit_id} className="border-b border-border last:border-0" data-testid={`fi-audit-${a.audit_id}`}>
              <td className="py-2.5 px-4"><Badge variant="outline" className="rounded-full text-[10px]">{a.action}</Badge></td>
              <td className="text-xs text-muted-foreground">{a.entity_type} · {a.entity_id}</td>
              <td className="text-xs"><p>{a.actor_email}</p><p className="text-[10px] text-muted-foreground">{a.actor_role}</p></td>
              <td className="px-4 text-xs text-muted-foreground">{fmtDateTime(a.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
